import {
  Control,
  useFieldArray,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
  UseFormSetError,
} from 'react-hook-form';
import api from 'lib/trpcClient';
import { investment as rules } from 'further-ui/validations';
import { sumBy } from 'lodash';
import { useLayoutContext } from 'contexts/LayoutContext';
import { InvestmentRevaluationOption } from 'further-types/lp/investment';
import { SortOrder } from 'further-types/utils';

export interface HoldingClassOption {
  id: string;
  title: string;
}
const useHoldingClasses = (
  {
    control,
    watch,
    setValue,
    getValues,
    setError,
  }: {
    control: Control<rules.CreateInvestmentFormValues>;
    watch: UseFormWatch<rules.CreateInvestmentFormValues>;
    setValue: UseFormSetValue<rules.CreateInvestmentFormValues>;
    getValues: UseFormGetValues<rules.CreateInvestmentFormValues>;
    setError: UseFormSetError<rules.CreateInvestmentFormValues>;
  },
  selectedAssetId?: string,
  isNewAsset?: boolean,
) => {
  const { selectedVehicleId } = useLayoutContext();
  const holdingClassesWatch = watch('holdingClasses');

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'holdingClasses',
  });

  const { data: holdingClasses } = api.holdingClass.list.useQuery(
    {
      assetId: selectedAssetId ?? '',
      order: SortOrder.Desc,
      orderBy: 'createdAt',
      vehicleId: selectedVehicleId ?? '',
    },
    {
      enabled: !!selectedAssetId && !isNewAsset,
    },
  );

  const holdingClassOptions =
    holdingClasses?.result.map((holdingClass) => ({
      id: holdingClass._id,
      title: holdingClass.name,
      currency: holdingClass.currency,
    })) ?? [];

  const selectedHoldingClassIds = holdingClassesWatch
    .map((hc) => hc.holdingClassId)
    .filter(Boolean);

  const getAvailableOptions = (currentIndex: number) => {
    return holdingClassOptions.filter(
      (option) =>
        !selectedHoldingClassIds.includes(option.id) ||
        option.id === holdingClassesWatch[currentIndex]?.holdingClassId,
    );
  };

  const addHoldingClass = () => {
    append({
      isNewHoldingClass: false,
      name: '',
      holdingClassId: '',
      investmentAmountTcy: 0,
      numberOfUnits: 0,
      unitPriceTcy: 0,
      currency: getValues('asset.baseCurrency') || getCurrency(),
    });
    setValue('revaluationOption', InvestmentRevaluationOption.ShareClassOnly);
  };

  const isNewHoldingClass = (index: number) => {
    return watch(`holdingClasses.${index}.isNewHoldingClass`);
  };

  const resetHoldingClassDropdown = (index: number) => {
    setValue(`holdingClasses.${index}.holdingClassId`, '');
    setValue('currencyRate', undefined);
  };

  const resetHoldingClassList = () => {
    replace([
      {
        isNewHoldingClass: false,
        name: '',
        holdingClassId: '',
        investmentAmountTcy: 0,
        numberOfUnits: 0,
        unitPriceTcy: 0,
        currency: getValues('asset.baseCurrency') || getCurrency(),
      },
    ]);
  };

  const isHoldingClassSelectDisabled = (index: number) => {
    return (
      (!!watch(`holdingClasses.${index}.isNewHoldingClass`) ||
        getAvailableOptions(index).length === 0 ||
        isNewAsset) ??
      false
    );
  };

  const removeHoldingClass = (index: number) => {
    if (fields.length === 2) {
      setValue('revaluationOption', InvestmentRevaluationOption.AllShares);
    }
    remove(index);
  };

  const calculateUnitPrice = (index: number) => {
    const investmentAmountTcy = getValues(
      `holdingClasses.${index}.investmentAmountTcy`,
    );
    const numberOfUnits = getValues(`holdingClasses.${index}.numberOfUnits`);
    if (investmentAmountTcy && numberOfUnits) {
      setValue(
        `holdingClasses.${index}.unitPriceTcy`,
        investmentAmountTcy / numberOfUnits,
      );
    } else {
      setValue(`holdingClasses.${index}.unitPriceTcy`, 0);
    }
  };

  const hasNewHoldingClass = () => {
    return holdingClassesWatch.some(
      (holdingClass) => holdingClass.isNewHoldingClass,
    );
  };

  const calculateTotalInvestmentAmount = () =>
    sumBy(holdingClassesWatch, 'investmentAmountTcy') || 0;

  const calculateTotalUnits = () =>
    sumBy(holdingClassesWatch, 'numberOfUnits') || 0;

  const calculateAverageUnitPrice = () => {
    const totalInvestment = calculateTotalInvestmentAmount();
    const totalUnits = calculateTotalUnits();
    return totalUnits ? totalInvestment / totalUnits : 0;
  };

  const getCurrency = () => {
    const holdingClasses = getValues('holdingClasses');
    return holdingClasses[0].currency;
  };

  const validateHoldingClassAmounts = () => {
    const values = getValues();
    let hasErrors = false;

    values.holdingClasses.forEach((holdingClass, index) => {
      if (holdingClass.investmentAmountTcy <= 0) {
        setError(`holdingClasses.${index}.investmentAmountTcy`, {
          type: 'custom',
          message: 'Investment amount must be greater than 0',
        });
        hasErrors = true;
      }

      if (holdingClass.numberOfUnits <= 0) {
        setError(`holdingClasses.${index}.numberOfUnits`, {
          type: 'custom',
          message: 'Number of units must be greater than 0',
        });
      }

      if (holdingClass.unitPriceTcy <= 0) {
        setError(`holdingClasses.${index}.unitPriceTcy`, {
          type: 'custom',
          message: 'Unit price must be greater than 0',
        });
        hasErrors = true;
      }
    });

    return !hasErrors;
  };

  return {
    fields,
    addHoldingClass,
    removeHoldingClass,
    getAvailableOptions,
    isNewHoldingClass,
    resetHoldingClassDropdown,
    resetHoldingClassList,
    isHoldingClassSelectDisabled,
    hasNewHoldingClass,
    calculateUnitPrice,
    calculateTotalInvestmentAmount,
    calculateTotalUnits,
    calculateAverageUnitPrice,
    getCurrency,
    validateHoldingClassAmounts,
  };
};

export default useHoldingClasses;
